import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useParams, useNavigate } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import { Button, Text, Small, Panel, H3, H4, Dropdown, Input, Tabs, Datepicker, Textarea } from '@bigcommerce/big-design';
import { AddIcon, ArrowDropDownIcon, AddCircleOutlineIcon, StoreIcon } from '@bigcommerce/big-design-icons';

import { purchaseOrdersUpdate, findPurchaseOrder } from '../../redux/actions/purchaseOrders';
import { suppliersIndex } from '../../redux/actions/suppliers';
import { taxSettingsIndex } from '../../redux/actions/taxSettings';
import { paymentTermsIndex } from '../../redux/actions/paymentTerms';
import { paymentMethodsIndex } from '../../redux/actions/paymentMethods';
import { locationsIndex } from '../../redux/actions/locations';

import AssignmentList from './AssignmentList';
import ProductSearch from './ProductSearch';
import ProductScan from './ProductScan';
import PurchaseOrderLine from './PurchaseOrderLine';
import PurchaseOrderTotals from './PurchaseOrderTotals';

import AddExternalItemModal from './AddExternalItemModal';
import UpdateExternalItemModal from './UpdateExternalItemModal';

const EditPurchaseOrderForm = ({ 
    payload,
    purchaseOrders,
    newPurchaseOrder,
    taxSettings,
    paymentTerms,
    paymentMethods,
    suppliers,
    loadTaxSettings,
    loadPaymentTerms,
    loadPaymentMethods,
    loadSuppliers,
    locations,  
    loadLocations,
    loadPurchaseOrder,
    purchaseOrderLines,
    updatePurchaseOrder,
    settings
}) => {  
  const { id } = useParams();
  let navigate = useNavigate();

  const [activeSupplier, setActiveSupplier] = useState(null);
  const [activeShippingAddress, setActiveShippingAddress] = useState(null);
  const [activePaymentTerm, setActivePaymentTerm] = useState(null);
  const [activePaymentMethod, setActivePaymentMethod] = useState(null);
  const [activeTaxSetting, setActiveTaxSetting] = useState(null);

  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [supplierOrderNumber, setSupplierOrderNumber] = useState(null);
  const [notes, setNotes] = useState(null);

  const [activeTab, setActiveTab] = useState('list');

  const [adjustedTotal, setAdjustedTotal] = useState(null);
  const [shippingTotal, setShippingTotal] = useState(null);
  const [expectedDate, setExpectedDate] = useState(null);
  const [paymentDate, setPaymentDate] = useState(null);

  const [submitted, setSubmitted] = useState(false);

  const [showAddExternalItemModal, setShowAddExternalItemModal] = useState(false);

  const [showUpdateExternalItemModal, setShowUpdateExternalItemModal] = useState(false);
  const [updateId, setUpdateId] = useState(null);
  const [updateName, setUpdateName] = useState(null);
  const [updatePrice, setUpdatePrice] = useState(null);
  const [updateSku, setUpdateSku] = useState(null);

  const [warnings, setWarnings] = useState({ moq: {}, orderMultiple: {} });

  useEffect(() => {
    loadPurchaseOrder(payload, id);
  }, []);

  useEffect(() => {
    if(newPurchaseOrder) { navigate(-1) };
  }, [newPurchaseOrder]);

  useEffect(() => {
    if(purchaseOrders.activePurchaseOrder) {
      loadTaxSettings(payload);
      loadPaymentTerms(payload);
      loadPaymentMethods(payload);
      loadSuppliers(payload);
      loadLocations(payload);

      setInvoiceNumber(purchaseOrders.activePurchaseOrder.invoice_number);
      setSupplierOrderNumber(purchaseOrders.activePurchaseOrder.supplier_order_number);
      setNotes(purchaseOrders.activePurchaseOrder.notes);
      setAdjustedTotal(purchaseOrders.activePurchaseOrder.adjusted_price);
      setShippingTotal(purchaseOrders.activePurchaseOrder.shipping_price);
      setExpectedDate(purchaseOrders.activePurchaseOrder.shipment_expected_at);
      setPaymentDate(purchaseOrders.activePurchaseOrder.payment_due_at);
      setInvoiceDate(purchaseOrders.activePurchaseOrder.created_at);
    }
  }, [purchaseOrders]);

  useEffect(() => {
    let newWarnings = { moq: {}, orderMultiple: {} };

    purchaseOrderLines.lineItems.forEach(lineItem => {
      let itemKey = lineItem.productId + '/' + lineItem.variantId;

      if(parseInt(lineItem.minimumOrder) > purchaseOrderLines.lineDetails[itemKey].quantity) {
        newWarnings.moq[itemKey] = `${lineItem.name || lineItem.productName} line quantity below MOQ: ${lineItem.minimumOrder} vs. ${purchaseOrderLines.lineDetails[itemKey].quantity}`
      }

      if(settings?.settings?.order_multiples) {
        if(lineItem.orderMultiple && ((purchaseOrderLines.lineDetails[itemKey].quantity % lineItem.orderMultiple) !== 0)) {
          newWarnings.orderMultiple[lineItem.productId + '/' + lineItem.variantId] = `${lineItem.name || lineItem.productName} line not divisible by assignment order multiple: ${lineItem.orderMultiple}`
        }
      }
    });

    setWarnings(newWarnings);
  }, [purchaseOrderLines]);

  useEffect(() => {
    if(purchaseOrders.activePurchaseOrder && paymentTerms.status === 'success') {
      let paymentTerm = paymentTerms.paymentTerms.find((term) => 
        term.id === purchaseOrders.activePurchaseOrder.payment_terms_id
      );

      setActivePaymentTerm(paymentTerm);
    }
  }, [paymentTerms]);

  useEffect(() => {
    if(purchaseOrders.activePurchaseOrder && paymentMethods.status === 'success') {

      let paymentMethod = paymentMethods.paymentMethods.find((method) => 
        method.id === purchaseOrders.activePurchaseOrder.payment_method_id
      );

      setActivePaymentMethod(paymentMethod);
    }
  }, [paymentMethods]);

  useEffect(() => {
    if(purchaseOrders.activePurchaseOrder && taxSettings.status === 'success') {
      console.log(purchaseOrders.activePurchaseOrder);

      let setting = taxSettings.taxSettings.find((taxSetting) => 
        taxSetting.id === purchaseOrders.activePurchaseOrder.tax_setting_id
      );

      setActiveTaxSetting(setting);
    }
  }, [taxSettings]);



  useEffect(() => {
    if(purchaseOrders.activePurchaseOrder && suppliers.status === 'success') {

      let supplier = suppliers.suppliers.find((supp) => 
        supp.id === purchaseOrders.activePurchaseOrder.supplier_id
      );

      setActiveSupplier(supplier);
    }
  }, [suppliers]);

  useEffect(() => {
    if(purchaseOrders.activePurchaseOrder && purchaseOrders.activePurchaseOrder.ship_to_type === 'restock' && locations.status === 'success') {

      let location = locations.locations.find((loc) => 
        loc.id === purchaseOrders.activePurchaseOrder.ship_to_address.id
      );

      location ||= locations.corporateLocations.locations.find((loc) =>
        loc.id === purchaseOrders.activePurchaseOrder.ship_to_address.id
      )

      setActiveShippingAddress(location);
    }

    if(purchaseOrders.activePurchaseOrder && purchaseOrders.activePurchaseOrder.ship_to_type !== 'restock' && locations.status === 'success') {
      setActiveShippingAddress({
        label: 'Customer Address',
        company: purchaseOrders.activePurchaseOrder.ship_to_address.company,
        first_name: purchaseOrders.activePurchaseOrder.ship_to_address.first_name,
        last_name: purchaseOrders.activePurchaseOrder.ship_to_address.last_name,
        street_1: purchaseOrders.activePurchaseOrder.ship_to_address.street_1,
        street_2: purchaseOrders.activePurchaseOrder.ship_to_address.street_2,
        city: purchaseOrders.activePurchaseOrder.ship_to_address.city,
        state: purchaseOrders.activePurchaseOrder.ship_to_address.state,
        zip: purchaseOrders.activePurchaseOrder.ship_to_address.zip
      });
    }
  }, [locations]);


  const purchaseOrderJson = () => {
    return {
      supplier: activeSupplier,
      shipping_address: activeShippingAddress,
      payment_term: activePaymentTerm,
      payment_method: activePaymentMethod,
      tax_setting: activeTaxSetting,
      invoice_number: invoiceNumber,
      supplier_order_number: supplierOrderNumber,
      notes: notes,
      invoice_date: invoiceDate,
      adjusted_total: adjustedTotal,
      shipping_total: shippingTotal || 0.0,
      expected_date: expectedDate,
      payment_due_date: paymentDate
    }
  }

  const purchaseOrderLinesJson = () => {
    let orderLines = [];

    purchaseOrderLines?.lineItems?.forEach(purchaseOrderLine => {
      let key = purchaseOrderLine.productId + '/' + purchaseOrderLine.variantId;
      let lineWithDetails = {...purchaseOrderLine};
      lineWithDetails.line_details = purchaseOrderLines.lineDetails[key];
      orderLines.push(lineWithDetails);
    })

    return orderLines;
  }


  const setFieldValue = (setField, event) => {
    let value = event.target.value;
    setField(value);
  }

  const setDropdownValue = (setField, value) => {
    setField(value);
  }

  const shipToAddressItems = () => {
    let items = [];

    locations.locations.map(location => 
      items.push(
        {
          content: location.label,
          onItemClick: () => setDropdownValue(setActiveShippingAddress, location),
          hash: location.id,
          icon: <StoreIcon />
        }
      )
    );

    locations.corporateLocations.locations.map(location => 
      items.push(
        {
         content: location.label,
         onItemClick: () => setDropdownValue(setActiveShippingAddress, location),
         hash: location.id 
        }
      )
    );

    return items;
  }

  const paymentTermsItems = () => {
    let items = [];

    paymentTerms.paymentTerms.map(paymentTerm => 
      items.push(
        {
          content: paymentTerm.term_name,
          onItemClick: () => setDropdownValue(setActivePaymentTerm, paymentTerm),
          hash: paymentTerm.id
        }
      )
    )

    return items;
  }

  const paymentMethodsItems = () => {
    let items = [];

    paymentMethods.paymentMethods.map(paymentMethod => 
      items.push(
        {
          content: paymentMethod.method_name,
          onItemClick: () => setDropdownValue(setActivePaymentMethod, paymentMethod),
          hash: paymentMethod.id
        }
      )
    )

    return items;
  }

  const taxSettingsItems = () => {
    let items = [];

    taxSettings.taxSettings.map(taxSetting => 
      items.push(
        {
          content: taxSetting.tax_name,
          onItemClick: () => setDropdownValue(setActiveTaxSetting, taxSetting),
          hash: taxSetting.id
        }
      )
    )

    return items;
  }

  const tabItems = [
    { ariaControls: 'content1', id: 'list', title: 'List Supplier Assignments' },
    { ariaControls: 'content2', id: 'search', title: 'Search BigCommerce' },
    { ariaControls: 'content3', id: 'scan', title: 'Scan Barcodes' }
  ];

  const showAddExternal = () => {
    setShowAddExternalItemModal(true);
  }

  const showUpdateExternal = (id, name, price, sku) => {
    setUpdateId(id);
    setUpdateName(name);
    setUpdatePrice(price);
    setUpdateSku(sku);
    setShowUpdateExternalItemModal(true);
  }

  const emptyToNull = (value) => {
    let emptyOrNull = value;
    
    if(value === '') {
      emptyOrNull = null;
    }

    return emptyOrNull;
  }

  const handleSubmit = () => {
    setSubmitted(true);
    updatePurchaseOrder(payload, id, purchaseOrderJson(), purchaseOrderLinesJson());
  }

  const validPO = () => {
    let block = !(activeShippingAddress && activePaymentTerm && activePaymentMethod && activeTaxSetting && expectedDate && paymentDate && invoiceDate && (purchaseOrderLines?.lineItems.length > 0))

    if(settings?.settings?.warnings_block) {
      if (Object.keys(warnings.moq).length > 0 || Object.keys(warnings.orderMultiple).length > 0) {
        block = true;
      }
    }

    return block;
  }

  const description = () => {
    return(
      <> 
        <Text>From this page, you can modify a purchase order.</Text>

        <Text>Set payment terms, a payment method and tax settings from their respective dropdowns. If options are missing, you can add them by going to the <Link to="/settings">Settings page</Link>.</Text>
        <Text>
          Add or update product/variant lines to this purchase order by selecting from a paginated set of products assigned to the selected supplier in the "List Supplier Assignments" tab, by searching BigCommerce in the "Search BigCommerce" tab, or by scanning barcodes in the "Scan Barcodes" tab.
          If products are missing from this supplier, you can add them by going to the <Link to="/assignments">Supplier Assignments page</Link>.
        </Text>
        <Text>Update additional information and notes in the "Totals, Adjustments and Dates" panel, then create the purchase order by clicking the "Update Purchase Order" button.</Text>

        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  const today = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm = today.getMonth() + 1; // months start at 0
    let dd = today.getDate();

    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;

    let todayDate = mm + '/' + dd + '/' + yyyy;

    return todayDate;
  }

  // const [purchaseOrderDate, setPurchaseOrderDate] = useState(today());
  const [invoiceDate, setInvoiceDate] = useState(today());

  const nextYear = () => {
    const today = new Date();
    const yyyy = today.getFullYear() + 1;
    let mm = today.getMonth() + 1; // months start at 0
    let dd = today.getDate();

    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;

    let next = mm + '/' + dd + '/' + yyyy;

    return next;
  }

  if(!purchaseOrders.activePurchaseOrder || !activeSupplier || !activePaymentTerm || !activePaymentMethod || taxSettings.isFetching) {
    return(
      <>
        <PageHeader stripeBackground='#c66628' helpLink="bigcommerce/create-and-update-purchase-orders/" />
        <SettingsPage title="Purchase Orders" description={description()}>
          <Panel>
            <H3>Edit Purchase Order</H3>
            <LoadingSpinner statusText="Loading Purchase Order"/>
          </Panel>
        </SettingsPage>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#c66628' helpLink="bigcommerce/create-and-update-purchase-orders/" />
      <SettingsPage title="Purchase Orders" description={description()}>
        <Panel>
          <H3>Edit Purchase Order</H3>

          <Panel>
            <H4>Supplier</H4>
            <div className="flex-container">
              <div className="flex-1">
                <H3>Supplier: { activeSupplier.supplier_name }</H3>
                <H4>Address</H4>
                <Text>{activeSupplier.address.company ? `${activeSupplier.address.company} - ` : ''}{activeSupplier.address.first_name} {activeSupplier.address.last_name}
                <br/>
                {activeSupplier.address.street_1}
                { activeSupplier.address.street_2 &&
                  <>
                    <br/>
                    {activeSupplier.address.street_2}
                  </>
                }
                <br/>
                {activeSupplier.address.city}, {activeSupplier.address.state}
                <br/>
                {activeSupplier.address.zip}</Text>
              </div>
              { activeShippingAddress && 
                <div className="flex-1 text-right left-15">
                  <H3>Ship To Address: { activeShippingAddress?.label }</H3>
                  <H4>Address</H4>
                  <Text>{activeShippingAddress.company ? `${activeShippingAddress.company} - ` : ''}{activeShippingAddress.first_name} {activeShippingAddress.last_name}
                  <br/>
                  {activeShippingAddress.street_1 || activeShippingAddress?.address?.address1}
                  { (activeShippingAddress.street_2 || activeShippingAddress?.address?.address2) &&
                    <>
                      <br/>
                      {activeShippingAddress.street_2 || activeShippingAddress?.address?.address2}
                    </>
                  }
                  <br/>
                  {activeShippingAddress.city || activeShippingAddress?.address?.city}, {activeShippingAddress.state || activeShippingAddress?.address?.state}
                  <br/>
                  {activeShippingAddress.zip || activeShippingAddress?.address?.zip}</Text>
                </div>
              }
            </div>
          </Panel>

          <Panel>
            <H3>Purchase Order Information</H3>

            <div className="flex-container space-between">
              { locations.status === 'success' &&
                <div className="flex-1">
                  <H4>Ship To Address</H4>
                  <Dropdown
                    items={shipToAddressItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{activeShippingAddress?.label || "Ship To Address"}</Button>}
                  />
                </div>
              }

              { paymentTerms.status === 'success' &&
                <div className="flex-1">
                  <H4>Payment Terms</H4>
                  <Dropdown
                    items={paymentTermsItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{activePaymentTerm?.term_name || "Payment Terms"}</Button>}
                  />
                </div>
              }

              { paymentMethods.status === 'success' &&
                <div className="flex-1">
                  <H4>Payment Method</H4>
                  <Dropdown
                    items={paymentMethodsItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{activePaymentMethod?.method_name || "Payment Method"}</Button>}
                  />
                </div>
              }

              { taxSettings.status === 'success' &&
                <div className="flex-1">
                  <H4>Tax Settings</H4>
                  <Dropdown
                    items={taxSettingsItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{activeTaxSetting?.tax_name || "Tax Setting"}</Button>}
                  />  
                </div>
              }
            </div>

            <hr/>

            <Input 
              required 
              label={`${settings.settings['invoice_number_override'] || "Invoice Number"}`}
              placeholder={`${settings.settings['invoice_number_override'] || "Invoice Number"}`}
              id="invoice_number" 
              type="text" 
              value={invoiceNumber} 
              onChange={(e) => setFieldValue(setInvoiceNumber, e)} 
            />

            <Input 
              required 
              label={`${settings.settings['purchase_order_number_override'] || "Purchase Order Number"}`}
              placeholder={`${settings.settings['purchase_order_number_override'] || "Purchase Order Number"}`}
              id="supplier_order_number" 
              type="text" 
              value={supplierOrderNumber} 
              onChange={(e) => setFieldValue(setSupplierOrderNumber, e)} 
            />
          </Panel>

          { purchaseOrders.activePurchaseOrder.ship_to_type === 'restock' &&
            <Panel>
              <H3>Add Purchase Order Lines</H3>
              <Small>Products selected here will appear in the purchase order lines in the panel below, and will be added to the purchase order.</Small>
              <Tabs
                activeTab={activeTab}
                aria-label="PO line item add"
                id="tab-po-lines"
                items={tabItems}
                onTabClick={setActiveTab}
              />
              { activeTab === 'list' &&
                <AssignmentList
                  payload={payload}
                  activeSupplier={activeSupplier}
                  activeShippingAddress={activeShippingAddress}
                />
              }
              { activeTab === 'search' &&
                <ProductSearch
                  payload={payload}
                  activeSupplier={activeSupplier}
                  activeShippingAddress={activeShippingAddress}
                />
              }
              { activeTab === 'scan' &&
                <ProductScan
                  payload={payload}
                  activeSupplier={activeSupplier}
                />
              }
            </Panel>
          }
          
          <Panel>
            <H3>Purchase Order Lines Settings</H3>
            { purchaseOrderLines?.lineItems?.map(purchaseOrderLine => 
              <PurchaseOrderLine
                purchaseOrderLine={purchaseOrderLine}
                purchaseOrderType={'direct-edit'}
                showSuggestionLevels={false}
                showUpdateExternal={showUpdateExternal}
              />

            )}


            <hr/>

            <Button
              variant='utility'
              onClick={() => showAddExternal()}
              iconLeft={<AddCircleOutlineIcon />}
            >
              Add external item
            </Button>
          </Panel>

          <Panel>
            <H3>Totals, Adjustments and Dates</H3>
            <div className="flex-container">
              <div className="flex-1">
                <H4>Significant Dates</H4>
                <Datepicker
                  required
                  label="Invoice Date"
                  locale="en-GB"
                  max={nextYear()}
                  min={today()}
                  onDateChange={(value) => setInvoiceDate(emptyToNull(value))}
                  value={`${invoiceDate.substring(0, 10)}T12:00:00.000Z`}
                />

                <Datepicker
                  required
                  label="Expected Date"
                  locale="en-GB"
                  max={nextYear()}
                  min={today()}
                  onDateChange={(value) => setExpectedDate(emptyToNull(value))}
                  value={`${expectedDate.substring(0, 10)}T12:00:00.000Z`}
                />

                <Datepicker
                  required
                  label="Payment Due Date"
                  locale="en-GB"
                  max={nextYear()}
                  min={today()}
                  onDateChange={(value) => setPaymentDate(emptyToNull(value))}
                  value={`${paymentDate.substring(0, 10)}T12:00:00.000Z`}
                />
              </div>

              <PurchaseOrderTotals
                purchaseOrderLines={purchaseOrderLines}
                activeSupplier={activeSupplier}
                taxSetting={taxSettings.taxSettings.find(setting => setting.id === activeSupplier.tax_setting_id)}
                adjustedTotal={adjustedTotal}
                shippingTotal={shippingTotal}
                setAdjustedTotal={setAdjustedTotal}
                setShippingTotal={setShippingTotal}
              />
            </div>
          </Panel>
          
          <Panel>
            <Textarea
              description="Order notes"
              label="Order Notes"
              onChange={(e) => setFieldValue(setNotes, e)}
              placeholder="Notes"
              resize={false}
              rows={3}
              value={notes}
            />
          </Panel>
          

          <Button 
            iconLeft={<AddIcon />}
            onClick={() => handleSubmit()}
            disabled={validPO() || submitted}
          >
            Update Purchase Order
          </Button>
          { activeSupplier && !activeShippingAddress &&
            <Text color="danger50">Purchase Order Must Specify a Shipping Address</Text>
          }
          { activeSupplier && !activePaymentTerm &&
            <Text color="danger50">Purchase Order Must Specify a Payment Term</Text>
          }
          { activeSupplier && !activePaymentMethod &&
            <Text color="danger50">Purchase Order Must Specify a Payment Method</Text>
          }
          { activeSupplier && !activeTaxSetting &&
            <Text color="danger50">Purchase Order Must Specify a Tax Setting</Text>
          }
          { activeSupplier && !invoiceDate &&
            <Text color="danger50">Purchase Order Must Include an Invoice Date</Text>
          }
          { activeSupplier && !expectedDate &&
            <Text color="danger50">Purchase Order Must Include an Expected Date</Text>
          }
          { activeSupplier && !paymentDate &&
            <Text color="danger50">Purchase Order Must Include an Expected Payment Date</Text>
          }
          { activeSupplier && !(purchaseOrderLines?.lineItems.length > 0) &&
            <Text color="danger50">Purchase Order Must Include Purchase Items</Text>
          }
          { Object.keys(warnings.moq).map(warningId => 
            <>
              <Text color="warning60">{warnings.moq[warningId]}</Text>
            </>
            )
          }
          { Object.keys(warnings.orderMultiple).map(warningId => 
            <>
              <Text color="warning60">{warnings.orderMultiple[warningId]}</Text>
            </>
            )
          }
        </Panel>
      </SettingsPage>
      <AddExternalItemModal
        payload={payload}
        showAddExternalItemModal={showAddExternalItemModal}
        setShowAddExternalItemModal={setShowAddExternalItemModal}
      />
      <UpdateExternalItemModal
        payload={payload}
        showUpdateExternalItemModal={showUpdateExternalItemModal}
        setShowUpdateExternalItemModal={setShowUpdateExternalItemModal}
        currentId={updateId}
        currentName={updateName}
        currentPrice={updatePrice}
        currentSku={updateSku}
      />
    </>
  )

}

const mapStateToProps = state => ({
  suppliers: state.suppliers,
  purchaseOrders: state.purchaseOrders,
  newPurchaseOrder: state.purchaseOrders.newPurchaseOrder,
  taxSettings: state.taxSettings,
  paymentTerms: state.paymentTerms,
  locations: state.locations,
  paymentMethods: state.paymentMethods,
  purchaseOrderLines: state.purchaseOrderLines,
  settings: state.settings,
});

const mapDispatchToProps = (dispatch) => ({
  loadTaxSettings: (payload) => dispatch(taxSettingsIndex(payload)),
  loadPaymentTerms: (payload) => dispatch(paymentTermsIndex(payload)),
  loadSuppliers: (payload) => dispatch(suppliersIndex(payload)),
  loadPaymentMethods: (payload) => dispatch(paymentMethodsIndex(payload)),
  loadLocations: (payload) => dispatch(locationsIndex(payload)),
  updatePurchaseOrder: (payload, id, purchaseOrderJson, purchaseOrderLinesJson) => dispatch(purchaseOrdersUpdate(payload, id, purchaseOrderJson, purchaseOrderLinesJson)),

  loadPurchaseOrder: (payload, id) => dispatch(findPurchaseOrder(payload, id, null)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditPurchaseOrderForm);